import { Col, Icon, Input, Row, Select } from "@visa/vds"
import { EMVCO_REGEX_EXPRESSION, IMPLEMENTATION_TYPE_PRODUCTS, PACKAGE_TYPE } from "_constants/CertificationTestingConstants"
import { DateHelper } from "_helpers/DateHelper"
import MCSControlCalendar from "components/MCSControlCalendar"
import MCSControlInput from "components/MCSControlInput"
import MCSControlSelect from "components/MCSControlSelect"
import MCSControlToggle from "components/MCSControlToggle"
import React, { useEffect, useState } from "react"
import CertificationPackageDialog from "./CertificationPackageDialog"
import GettingStartedQuestionnaire from "pages/GettingStarted/Components/GettingStartedQuestionnaire"

const CertificationPackageInformation = ({
    control, getValues, register, watch, reset,
    handleChange, isReadOnly,
    loadedConstants, originalRequiredDocuments,
    originalImplementationTypeId
}) => {
    const { implementationTypeList, requirementDocumentList, reopenReasonList, protocolVersionList } = loadedConstants
    const packageType = getValues("packageType")
    const watchComponent = watch("component")
    const [tempComponent, setTempComponent] = useState(watchComponent)
    const [tempImplementationType, setTempImplementationType] = useState(getValues("implementationTypeId"))

    const [dialog, setDialog] = useState(false);

    const [openChangeImplementationTypeDialog, setOpenChangeImplementationTypeDialog] = useState(false)
    const [deletedDocuments, setDeletedDocuments] = useState([])


    const getImplementationTypeWithRequirements = (newImplementationTypeId) => {
        return implementationTypeList.find((item) => item.id == newImplementationTypeId)
    }

    const getRequirementDocument = (requirement) => {
        return requirementDocumentList.find((item) => item.id == requirement)
    }

    const createNewRequirementDocuments = (newImplementationTypeId, previousRequiredDocuments) => {
        if (newImplementationTypeId == null) return [];
        const newImplementationTypeWithRequirements = getImplementationTypeWithRequirements(newImplementationTypeId)
        const newRequirementsWithFootnotes = newImplementationTypeWithRequirements.requirementFootnotes

        return newRequirementsWithFootnotes.map((requirementFootnote, idx) => {
            let previousDocument = previousRequiredDocuments.find((document) => document.requirement == requirementFootnote.requirement)
            if (previousDocument != null) {
                return { ...previousDocument, footnote: requirementFootnote.footnote } // same requirement but might be a different footnote
            } else {
                return {
                    footnote: requirementFootnote.footnote,
                    requirement: requirementFootnote.requirement,
                    requirementName: getRequirementDocument(requirementFootnote.requirement).name,
                    fileName: null,
                    hasFile: false
                }
            }
        })
    }

    const handleImplementationTypeIdChange = (paramI = null, paramC = null) => { // necessary params due to a delay in states update

        let implementationTypeUsed = paramI ?? tempImplementationType
        let componentUsed = paramC ?? tempComponent

        const previousRequiredDocuments = getValues("certificationPackagePageRequiredDocuments")
        const newRequiredDocuments = createNewRequirementDocuments(implementationTypeUsed, previousRequiredDocuments ?? [])

        reset({
            ...getValues(),
            component: componentUsed,
            implementationTypeId: implementationTypeUsed,
            certificationPackagePageRequiredDocuments: originalImplementationTypeId == implementationTypeUsed ? originalRequiredDocuments : newRequiredDocuments,
        })
    }

    const getRemovedRequirementDocuments = (newImplementationTypeId) => {
        const currentRequirementDocuments = getValues("certificationPackagePageRequiredDocuments") ?? []
        if (newImplementationTypeId == null) return currentRequirementDocuments;

        const newImplementationTypeWithRequirements = getImplementationTypeWithRequirements(newImplementationTypeId)

        const newRequirementsWithFootnotes = newImplementationTypeWithRequirements ? newImplementationTypeWithRequirements.requirementFootnotes : []
        const requirements = newRequirementsWithFootnotes.map((requirementFootnote, idx) => requirementFootnote.requirement)

        const removedRequirementDocuments = currentRequirementDocuments.filter((doc) => !requirements.includes(doc.requirement))
        return removedRequirementDocuments.filter((doc) => doc.hasFile)
    }

    const checkIfDocumentsAreDeleted = (newImplementationTypeId, defaultComponent = null) => {

        setTempComponent(defaultComponent ?? getValues("component"))
        setTempImplementationType(newImplementationTypeId)
        setDeletedDocuments([])
        const removedDocuments = getRemovedRequirementDocuments(newImplementationTypeId)

        if (removedDocuments.length > 0) {
            setDeletedDocuments(removedDocuments)
            setOpenChangeImplementationTypeDialog(true)
            return
        }
        handleImplementationTypeIdChange(newImplementationTypeId, defaultComponent ?? getValues("component"))
    }

    const handleComponentChange = (newComponent) => {
        checkIfDocumentsAreDeleted(null, parseInt(newComponent))
    }

    const generateImplementationTypeLabel = () => {
        let implementationTypeId = getValues().implementationTypeId
        if (implementationTypeId == null) return ""

        let { typeId, label } = implementationTypeList.find(x => x.id === implementationTypeId)
        return `${typeId} (${label})`
    }

    useEffect(() => { }, [tempComponent, tempImplementationType])

    return (
        <>
            {dialog && <GettingStartedQuestionnaire setDialog={setDialog} checkIfDocumentsAreDeleted={checkIfDocumentsAreDeleted} component={getValues("component")} />}
            <CertificationPackageDialog title="Warning - Changing your implementation type will remove uploaded documents"
                dialogType="warning" open={openChangeImplementationTypeDialog} setOpen={setOpenChangeImplementationTypeDialog}
                leftButton={{ color: "secondary", text: "Change Implementation Type", handleClick: () => handleImplementationTypeIdChange(null) }}
                rightButton={{ color: "primary", text: "Close" }}
            >
                {deletedDocuments && <ul>{deletedDocuments.map((doc, idx) => <li key={idx}>{doc.requirementName}</li>)}</ul>}
                You will need to re-upload these documents if you choose to revert back to the same implementation type
            </CertificationPackageDialog>
            <Row>
                <Col>
                    <span className="font-weight-bold">Package Type</span><br />
                    <span>{packageType === PACKAGE_TYPE.NEW ? "New" : "Reopen"}</span>
                </Col>
                <Col>
                    <MCSControlInput
                        readOnly={isReadOnly} register={register} control={control} handleChange={handleChange}
                        label="Package Name" name={"packageName"} rules={{ maxLength: 25 }}
                    />
                </Col>
            </Row>
            {packageType === PACKAGE_TYPE.REOPEN &&
                <>
                    <Row>
                        <Col>
                            <span className="font-weight-bold">Previous Certification Package ID</span><br />
                            <span>{getValues("previousCertificationPackageId")}</span>
                        </Col>
                        <Col>
                            <span className="font-weight-bold">Visa Secure Compliance ID of previous certification</span><br />
                            <span>{getValues("previousComplianceId")}</span>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            {isReadOnly && <Select label="Reopen Reason*" readOnly={true} value={reopenReasonList && reopenReasonList.find(x => x.id === getValues().reopenReasonId)?.description} />}
                            {!isReadOnly && <MCSControlSelect
                                readOnly={false} register={register} control={control} handleChange={handleChange}
                                name={"reopenReasonId"} label="Reopen Reason*"
                                options={<>{reopenReasonList && reopenReasonList.map((reason, idx) => <option value={reason.id}>{reason.description}</option>)}</>}
                                rules={{ required: true }}
                            />
                            }
                        </Col>
                        <Col />
                    </Row>
                </>}
            <Row>
                <Col>
                    <MCSControlToggle readOnly={packageType === PACKAGE_TYPE.REOPEN || isReadOnly} register={register} control={control} handleChange={(e) => handleComponentChange(e.target.value)}
                        name={"component"} label={"Component*"}
                        buttons={[
                            { name: "acs", id: "acs", label: "ACS", value: IMPLEMENTATION_TYPE_PRODUCTS.ACS },
                            { name: "3dss", id: "3dss", label: "3DSS", value: IMPLEMENTATION_TYPE_PRODUCTS["3DS"] }
                        ]}
                    />
                </Col>
                <Col className="certification-package-implementation-select">
                    {(isReadOnly || packageType === PACKAGE_TYPE.REOPEN) &&
                        <Input
                            label="Implementation Type ID*"
                            readOnly={true}
                            value={generateImplementationTypeLabel()}
                        />
                    }
                    {(!isReadOnly && packageType !== PACKAGE_TYPE.REOPEN) &&
                        <MCSControlInput
                            name={"implementationTypeId"}
                            value={generateImplementationTypeLabel()}
                            register={register}
                            readOnly={true}
                            control={control}
                            label={
                                <Row>
                                    <Col className="certification-package-do-you-know-implementation-type-left-col">Implementation Type ID*</Col>
                                    <Col className="padding-left-0px padding-bottom-0px certification-package-do-you-know-implementation-type-right-col" onClick={() => setDialog(true)}>
                                        <span className="cursor-pointer certification-package-change-choose-implementation-type-text">
                                            {getValues().implementationTypeId ? "CHANGE your Implementation Type" : "CHOOSE your Implementation Type"}
                                        </span>
                                        <span className="certification-package-company-information-update-icon"> <Icon name="maximize" resolution="tiny" aria-label="maximize" /></span>
                                    </Col>
                                </Row>
                            }
                            placeholder="Implementation Type Needed"
                            useNativePlaceholder={true}
                            rules={{
                                required: true,
                            }}
                        />
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    <MCSControlInput
                        readOnly={isReadOnly} control={control} handleChange={handleChange}
                        label="EMVCo Reference Number*" name={"emvCoRefNum"}
                        rules={{
                            maxLength: 100,
                            required: true,
                            validate: { validateEmvCoRefNum: (v) => watchComponent === IMPLEMENTATION_TYPE_PRODUCTS.ACS ? EMVCO_REGEX_EXPRESSION.ACS.test(v) : EMVCO_REGEX_EXPRESSION["3DS"].test(v) }
                        }}
                    />
                </Col>
                <Col>
                    {isReadOnly && <Select label="EMVCo Expiry Date*" readOnly={true} value={DateHelper.ToFormattedDate(getValues().emvCoExpiryDate, "dd MMM yyyy")} />}
                    {!isReadOnly && <MCSControlCalendar
                        readOnly={true} register={register} control={control} handleChange={handleChange}
                        name="emvCoExpiryDate" label="EMVCo Expiry Date*" allowPastDates={true}
                        rules={{ required: true }}
                    />}
                </Col>
            </Row>
            <Row>
                <Col>
                    {(isReadOnly || packageType === PACKAGE_TYPE.REOPEN) && <Select label="Protocol Version*" readOnly={true} value={protocolVersionList && protocolVersionList.find(x => x.id === getValues().protocolVersionId).description} />}
                    {(!isReadOnly && packageType !== PACKAGE_TYPE.REOPEN) && <MCSControlSelect
                        register={register} control={control} handleChange={handleChange}
                        name={"protocolVersionId"} label="Protocol Version*"
                        options={<>{protocolVersionList && protocolVersionList.map((version, idx) => <option value={version.id}>{version.description}</option>)}</>}
                    />}
                </Col>
                <Col>
                    <MCSControlInput
                        readOnly={isReadOnly} register={register} control={control} handleChange={handleChange}
                        label="Alternate Company Name to be listed on Letter" name={"altCompanyName"}
                        rules={{ maxLength: 500 }}
                    />
                </Col>
            </Row>
        </>
    )
}

export default CertificationPackageInformation